import { NotifyHelper } from 'classes/helpers/notify.helper';
import { t } from 'i18next';
import { BallType } from 'lib_ts/enums/machine.enums';
import { ReferenceListType } from 'lib_ts/enums/pitches.enums';
import { Controller } from 'lib_ts/enums/routing/controllers';
import { PitchesRoutes } from 'lib_ts/enums/routing/routes/pitches';
import { IServerResponse } from 'lib_ts/interfaces/common/i-server-response';
import { IPitchList } from 'lib_ts/interfaces/pitches';
import { IPitch, ITrainingPitch } from 'lib_ts/interfaces/pitches/i-pitch';
import { ISearchPitches } from 'lib_ts/interfaces/pitches/i-search-pitches';
import { BaseRESTService } from 'services/_base-rest.service';

export class PitchesService extends BaseRESTService {
  private static instance: PitchesService;
  static getInstance(): PitchesService {
    if (!PitchesService.instance) {
      PitchesService.instance = new PitchesService();
    }

    return PitchesService.instance;
  }

  private constructor() {
    super({
      controller: Controller.Pitches,
    });
  }

  async getOne(id: string): Promise<IPitch | undefined> {
    return await this.get({
      uri: PitchesRoutes.GetOne,
      params: {
        pitch_id: id,
      } as any,
    });
  }

  async getList(listID: string, search?: ISearchPitches): Promise<IPitch[]> {
    return await this.post(
      {
        uri: PitchesRoutes.GetList,
        params: {
          id: listID,
          traj: true,
        } as any,
      },
      search
    );
  }

  async getReferenceListWithPitches(
    ball_type: BallType,
    type: ReferenceListType
  ): Promise<{
    list: IPitchList;
    pitches: ITrainingPitch[];
  }> {
    return await this.get({
      uri: PitchesRoutes.GetReferenceList,
      params: {
        ball_type: ball_type,
        type: type,
      } as any,
    });
  }

  async search(data: ISearchPitches, limit: number): Promise<IPitch[]> {
    return await this.post(
      {
        uri: PitchesRoutes.Search,
        params: {
          limit: limit,
        } as any,
      },
      data
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? t('common.request-failed-msg'),
          });
          return [];
        }

        return result.data as IPitch[];
      })
      .catch((reason) => {
        console.error(reason);
        NotifyHelper.error({
          message_md: t('common.request-failed-msg'),
        });
        return [];
      });
  }

  async createOne(data: Partial<IPitch>): Promise<IPitch | undefined> {
    return await this.post(
      {
        uri: PitchesRoutes.CreateOne,
      },
      data
    ).then((result: IServerResponse) => {
      if (!result.success) {
        return undefined;
      }

      return result.data as IPitch;
    });
  }

  async updateMany(data: Partial<IPitch>[]): Promise<IServerResponse> {
    if (data.findIndex((p) => !p._id) !== -1) {
      throw new Error('Cannot put without id');
    }

    return await this.put(
      {
        uri: PitchesRoutes.UpdateMany,
      },
      data
    );
  }

  async markAsDeleted(ids: string[]): Promise<IServerResponse> {
    return await this.post(
      {
        uri: PitchesRoutes.MarkAsDeleted,
      },
      { ids }
    );
  }
}
